import { Exercise as ComponentExercise, ExerciseType, VideoData } from "@/services/admin/exercises.interfaces";
import { EditorInterface } from "./EditorInterface";
import { Video as VideoIcon } from "lucide-react";

/**
 * Editor para exercícios de vídeo (versão unificada)
 * Combina as funcionalidades duplicadas das implementações anteriores
 */
export class VideoInfo extends EditorInterface {
  /**
   * Retorna as informações para exibir no cartão do exercício
   * @param exercise Objeto do exercício
   */
  static GetCardInfo(exercise: ComponentExercise): {
    title: string;
    subtitle: string;
    description?: string;
  } {
    if (!exercise || (exercise.type !== ExerciseType.Video && !VideoInfo.SupportsExerciseType(exercise.type as unknown as string))) {
      return {
        title: 'Vídeo',
        subtitle: 'Sem detalhes',
      };
    }

    try {
      // Tenta extrair os dados do exercício, lidando com diferentes formatos
      let videoUrl = '';
      let title = 'Sem título';
      let description = '';
      
      // Se os dados estiverem no formato aninhado (data é um objeto)
      if (exercise.data && typeof exercise.data === 'object') {
        const data = exercise.data as unknown as VideoData;
        videoUrl = data.videoUrl || data.video || '';
        title = data.title || 'Sem título';
        description = data.description || '';
      } 
      // Se os dados estiverem no formato plano
      else {
        videoUrl = (exercise as any).videoUrl || (exercise as any).video || '';
        title = (exercise as any).title || 'Sem título';
        description = (exercise as any).description || '';
      }
      
      // Formata o subtitle para exibir o título do vídeo
      const subtitle = title.length > 0 ? title : 'Sem título';
      
      // Formata a descrição para exibir a URL e a descrição, se existirem
      let cardDescription = '';
      
      if (videoUrl) {
        // Extrai apenas o ID ou nome do vídeo para uma exibição mais limpa
        const videoId = VideoInfo.extractVideoIdentifier(videoUrl);
        cardDescription += `Vídeo: ${videoId || videoUrl}`;
      }
      
      if (description) {
        if (cardDescription) cardDescription += ' | ';
        cardDescription += description.length > 100 
          ? `${description.substring(0, 100)}...` 
          : description;
      }
      
      return {
        title: "Vídeo",
        subtitle,
        description: cardDescription || undefined,
      };
    } catch (error) {
      console.error("Erro ao processar dados do exercício de vídeo:", error);
      return {
        title: 'Vídeo',
        subtitle: 'Erro ao processar dados',
      };
    }
  }
  
  /**
   * Extrai um identificador mais amigável de uma URL de vídeo
   * @param url URL do vídeo
   */
  private static extractVideoIdentifier(url: string): string {
    try {
      // Para URLs do YouTube
      const youtubeMatch = url.match(/(?:v=|\/)([\w-]{11})(?:\?|&|\/|$)/);
      if (youtubeMatch) return `YouTube: ${youtubeMatch[1]}`;
      
      // Para URLs do Vimeo
      const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
      if (vimeoMatch) return `Vimeo: ${vimeoMatch[1]}`;
      
      // Se não for possível identificar, retorna uma versão resumida da URL
      return url.length > 30 ? `${url.substring(0, 30)}...` : url;
    } catch {
      return url;
    }
  }
  
  /**
   * Retorna o título do tipo de exercício
   */
  static getTitle(): string {
    return "Vídeo";
  }
  
  /**
   * Retorna uma descrição formatada para o exercício
   * @param exercise Objeto do exercício
   */
  static getDescription(exercise: ComponentExercise): string {
    if (!exercise) {
      return "Sem detalhes";
    }
    
    try {
      // Tenta extrair a URL do vídeo do exercício, lidando com diferentes formatos
      let videoUrl = '';
      
      // Se os dados estiverem no formato aninhado (data é um objeto)
      if (exercise.data && typeof exercise.data === 'object') {
        const data = exercise.data as unknown as VideoData;
        videoUrl = data.videoUrl || data.video || '';
      } 
      // Se os dados estiverem no formato plano
      else {
        videoUrl = (exercise as any).videoUrl || (exercise as any).video || '';
      }
      
      return videoUrl ? `Vídeo: ${VideoInfo.extractVideoIdentifier(videoUrl)}` : "Sem vídeo definido";
    } catch (error) {
      console.error("Erro ao processar dados do exercício:", error);
      return "Erro ao processar dados";
    }
  }
  
  /**
   * Retorna o ícone para o tipo de exercício
   */
  static getIcon() {
    return VideoIcon;
  }
  
  /**
   * Verifica se o exercício é do tipo Vídeo
   */
  static SupportsExerciseType(type: string): boolean {
    return type === ExerciseType.Video || type === 'watch_video';
  }
}